import type {
  SurveyCommentsRequest,
  SurveyListRequest,
  SurveyResponsesRequest,
  SurveyResultsCut,
} from '../Types/Surveys.types';

/**
 * Every result key carries the version.
 *
 * Results are always scoped to one version (R2/R21): caching them under the survey id alone
 * would show the numbers of version 1 under the heading of version 2 the moment someone changed
 * the filter.
 */
export const surveysQueryKeys = {
  all: ['surveys'] as const,
  lists: () => [...surveysQueryKeys.all, 'list'] as const,
  list: (params: SurveyListRequest) => [...surveysQueryKeys.lists(), params] as const,
  details: () => [...surveysQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...surveysQueryKeys.details(), id] as const,
  /**
   * The cut is part of the key, for the same reason the version is.
   *
   * Cut numbers and full-base numbers are two different populations under the same heading (R49),
   * and caching them together would print one under the other's label the moment the cut moved —
   * exactly the "30% or 75% depending on the base you imagine" this feature exists to end.
   */
  results: (id: string, version?: number, cut?: SurveyResultsCut) =>
    [
      ...surveysQueryKeys.all,
      'results',
      id,
      version ?? 'delivered',
      cut ? `${cut.filterQuestionKey}=${cut.filterOptionValue}` : 'full',
    ] as const,
  responses: (id: string, params: SurveyResponsesRequest) =>
    [...surveysQueryKeys.all, 'responses', id, params] as const,
  /**
   * Every version and every filter of one survey, as invalidation prefixes.
   *
   * Erasing one person's answers crosses every version (R27), so invalidating the version on screen
   * would leave the other versions — and every other page and status filter — holding a row the BFF
   * has already deleted. A prefix is the only honest scope for that.
   */
  allResultsOf: (id: string) => [...surveysQueryKeys.all, 'results', id] as const,
  allResponsesOf: (id: string) => [...surveysQueryKeys.all, 'responses', id] as const,
  allCommentsOf: (id: string) => [...surveysQueryKeys.all, 'comments', id] as const,
  comments: (id: string, params: SurveyCommentsRequest) =>
    [...surveysQueryKeys.all, 'comments', id, params] as const,
};
